import React, { useState } from "react";
import AddPartnerModal from "./AddPartner";
import { deletPartner, updatePartner } from "../../services/partnerService";
import InvestmentHistoryModal from "./InvestmentHistory";
import PartnerProfileModal from "./PartnerProfileModal";

export default function PartnersTable({
  partnersData = [],
  getPartnerRefresh,
  partnersLoading,
}) {
  const [editPartner, setEditPartner] = useState(null);
  const [loading, setLoading] = useState(false);
  const [historyPartner, setHistoryPartner] = useState(null);
  const [partnerProfile, setPartnerProfile] = useState(null);

  const nfPKR = new Intl.NumberFormat("en-PK", {
    style: "currency",
    currency: "PKR",
    minimumFractionDigits: 0,
  });

  const nfMetal = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 3,
  });

  const handleDelete = async (p) => {
    // Show SweetAlert2 confirmation dialog
    const res = await Swal.fire({
      icon: "warning",
      title: "Delete Partner?",
      text: "Partner profile + all their investments will be deleted. \nInventory won't be affected. Continue?",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
    });

    // Handle the result
    if (res.isConfirmed) {
      // The user confirmed the deletion
      const response = await deletPartner(p?._id);
      if (response.success) {
        getPartnerRefresh();
      } else {
        alert("Unknown error occured while deleting the partner.");
      }
    } else {
      // The user canceled
      console.log("Partner not deleted");
    }
  };

  return (
    <>
      <div className="container-fluid">
        <div className="row">
          <div className="col-12 shadow section-capital">
            <div className="table-responsive tableScroll">
              <table className="table border border-light">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Partner Name</th>
                    <th>Contributions</th>
                    <th>Gold (grams)</th>
                    <th>Silver (grams)</th>
                    <th>Cash (PKR)</th>
                    <th>Total</th>
                    <th>Action</th>
                    <th>History</th>
                  </tr>
                </thead>

                <tbody>
                  {partnersLoading ? (
                    <tr>
                      <td colSpan="9" className="text-center text-muted py-3">
                        Loading...
                      </td>
                    </tr>
                  ) : partnersData.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="text-center text-muted py-3">
                        No partners yet. Add Partner first.
                      </td>
                    </tr>
                  ) : (
                    partnersData?.map((p, idx) => {
                      return (
                        <tr key={p.id}>
                          <td>{idx + 1}</td>

                          <td>{p.name}</td>

                          <td>{p.totals?.contributions || 0}</td>

                          <td>{nfMetal.format(p.totals.goldGram)}</td>

                          <td>{nfMetal.format(p.totals.silverGram)}</td>

                          <td>{nfPKR.format(p.totals.cashPKR)}</td>

                          <td>{nfPKR.format(p.totals.totalPKR)}</td>

                          <td>
                            <div
                              className="btn btnEdit ms-2 mt-1"
                              data-action="edit"
                              data-id={p.id}
                              onClick={() => setEditPartner(p)}
                            >
                              <i className="bi bi-pencil"></i>
                            </div>

                            <div
                              className="btn btnDelete ms-2 mt-1"
                              data-action="delete"
                              data-id={p.id}
                              onClick={() => {
                                handleDelete(p);
                              }}
                            >
                              <i className="bi bi-trash3"></i>
                            </div>

                            <div
                              className="btn btnProfile ms-2 mt-1"
                              data-action="profile"
                              data-id={p.id}
                              onClick={() => setPartnerProfile(p)}
                            >
                              <i className="bi bi-person-circle"></i>
                            </div>
                          </td>

                          <td>
                            <div
                              className="btn btnHistory ms-2 mt-1"
                              data-action="history"
                              data-id={p.id}
                              onClick={() => setHistoryPartner(p)}
                            >
                              <i className="bi bi-clock-history"></i>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <AddPartnerModal
        isEdit={editPartner !== null}
        show={editPartner !== null}
        onClose={() => setEditPartner(null)}
        onSave={async (editPartner) => {
          const updatedFields = {
            name: editPartner?.name,
            ownershipType: editPartner?.ownershipType,
            profitRatio: editPartner?.profitRatio,
            cnic: editPartner?.cnic,
            contact: editPartner?.contact,
            address: editPartner?.address,
          };

          try {
            setLoading(true);

            // return console.log({ updatedFields });

            const response = await updatePartner(
              editPartner?.id,
              updatedFields,
            );
            if (response.success) {
              getPartnerRefresh();
              return { success: true, message: "Updated Successfully" };
            }

            return { success: true, error: "Unknown Error Occured." };
          } catch (err) {
            const error =
              err.response?.data?.message || "Error while creating the partner";
            return {
              success: false,
              error,
            };
          } finally {
            setLoading(false);
          }
        }}
        editPartner={editPartner}
        isLoading={loading}
      />

      <InvestmentHistoryModal
        show={historyPartner !== null}
        onClose={() => setHistoryPartner(null)}
        partner={historyPartner}
        partners={partnersData}
        setPartner={setHistoryPartner}
        getPartnerRefresh={getPartnerRefresh}
      />

      <PartnerProfileModal
        show={partnerProfile !== null}
        onClose={() => setPartnerProfile(null)}
        partner={partnerProfile}
      />
    </>
  );
}

function escapeHtml(str) {
  return String(str ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}
